import { Box, CircularProgress } from '@mui/material';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import { ProductWarehouseHistoryLinkTabTable } from 'features/cost-analysis';
import { TenantPage } from 'layouts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { equipmentService } from 'shared/services';
import toast from 'shared/utils/toast';
import EquipmentToCostDialog from '../components/EquipmentToCostDialog';
import { AccessoriesTab } from '../components/tabs-component/Accessories';
import { ContractEquipmentTab } from '../components/tabs-component/ContractEquipment';
import { CostAnalysTab } from '../components/tabs-component/CostAnalys';
import EquipmentForm from '../components/tabs-component/EquipmentForm';
import { HistoryTab } from '../components/tabs-component/History';
import { LogBook } from '../components/tabs-component/log-book';
import { EquipmentLinkMode, IEquipment } from '../types';

/**
 * Predefined equipment document fields that support upload/download.
 */
const DOWNLOADABLE_FIELDS = [
  'technical_file',
  'user_manual',
  'maintenance_checklist_template',
  'preemptive_maintenance_calendar',
  'ce_ivd'
];

export const EditEquipment = () => {
  const { t } = useTranslation();
  const { equipmentId } = useParams();
  const translationPrefix = 'pages.equipment.edit';

  const [equipment, setEquipment] = useState<IEquipment>();
  const [mode, setMode] = useState<EquipmentLinkMode>('');

  const [loading, setLoading] = useState<boolean>(true);

  const isDisabledEdit =
    useIsTechnicianCoordinator() || useIsTechnician() || useIsDirector() || useIsSupplier();

  const visible = useIsTenantAdministrator();
  const numericEquipmentId = equipmentId ? Number(equipmentId) : NaN;

  const setModeWrapper = (newMode: EquipmentLinkMode) => {
    setMode(newMode || '');
  };

  const fetchEquipment = useCallback(() => {
    if (!isNaN(numericEquipmentId)) {
      setLoading(true);
      equipmentService
        .get(numericEquipmentId)
        .then((res) => setEquipment(res))
        .catch(() => toast.error(t('errors.failed-to-load')))
        .finally(() => setLoading(false));
    } else {
      console.warn('[DEBUG EditEquipment] fetchEquipment saltato: numericEquipmentId è NaN');
    }
  }, [numericEquipmentId]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const updateEquipment = async (values: any) => {
    if (!equipment?.id) {
      return;
    }

    try {
      equipmentService
        .update(equipment.id, {
          ...values,
          market_value: values['market_value'] && values['market_value'].replace(',', '.')
        })
        .then(() => {
          toast.success('Strumento aggiornato con successo');
          fetchEquipment();
        });
    } catch {
      toast.error(t('Fallito aggiornamento strumento.'));
    }
  };

  const handleRemoveLink = (id: number, pars: string) => {
    if (!isNaN(numericEquipmentId)) {
      equipmentService.removeCostLink(+equipmentId, { model: pars, object_id: id }).then(() => {
        fetchEquipment();
        toast.success(t(translationPrefix + '.price-origin-removed-success'));
      });
    }
  };

  const tabConfig: TabItem[] = useMemo(() => {
    return [
      {
        label: t(`global.base-info`),
        component: (
          <EquipmentForm
            equipment={equipment}
            onSubmit={updateEquipment}
            disableEdit={isDisabledEdit}
          />
        )
      },
      {
        label: t(`${translationPrefix}.accessories`),
        visible: visible,
        variant: 'admin',
        component: <AccessoriesTab equipment={equipment} />
      },
      {
        label: t(`${translationPrefix}.contracts`),
        component: <ContractEquipmentTab equipment={equipment} />
      },
      {
        label: t(`${translationPrefix}.logbook`),
        component: <LogBook equipment={equipment} />,
        disablePadding: true
      },
      {
        label: t(`${translationPrefix}.cost-analys`),
        visible: visible,
        variant: 'admin',
        component: (
          <CostAnalysTab
            equipment={equipment}
            isDisabledEdit={isDisabledEdit}
            setMode={setModeWrapper}
            handleRemoveLink={handleRemoveLink}
          />
        )
      },
      {
        label: t(`global.history-update`),
        visible: visible,
        variant: 'admin',
        component: (
          <ProductWarehouseHistoryLinkTabTable
            input_params={{ entity_code: equipment?.code, entity_type: '2' }}
          />
        )
      },
      {
        label: t(`${translationPrefix}.history`),
        visible: visible,
        variant: 'admin',
        component: <HistoryTab equipment={equipment} />
      }
    ];
  }, [equipment, isDisabledEdit, equipmentId, visible]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TenantPage
      title={`${t(`${translationPrefix}.title`)} ${equipment?.code ? `${equipment?.code}` : ''}`}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={equipment?.code}>
      {equipment && (
        <Box>
          <EnhancedTabs
            tabs={tabConfig}
            variant="scrollable"
            documentConfig={{
              serviceName: 'equipment',
              entityId: equipment?.id,
              dictParams: { equipment: equipmentId },
              customFolderName: `menu.equipments/${equipment?.code}`,
              folderDescription: `Descrizione: ${equipment?.description}; Seriale: ${equipment?.serial_number}; `,
              disableEdit: isDisabledEdit
            }}
            attachmentsConfig={{
              fields: DOWNLOADABLE_FIELDS,
              entity: equipment,
              service: equipmentService,
              translationPrefix: 'entities.equipment'
            }}
          />

          <EquipmentToCostDialog
            equipmentId={+equipmentId}
            mode={mode}
            setMode={setMode}
            callback={fetchEquipment}
          />
        </Box>
      )}
    </TenantPage>
  );
};

export default EditEquipment;
